import {
  QueryV1,
  BoolQueryV1,
  TermsQueryV1,
  RangeQueryV1,
  SortingV1
} from '@ttab/elephant-api/index'
import { eachDayOfInterval, endOfDay, format, startOfDay } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import { fields as articleFields } from '@/shared/schemas/article'
import { fields as planningFields } from '@/shared/schemas/planning'
import { fields as eventFields } from '@/shared/schemas/event'
import { memoExtraFields, memoSourceExtraFields } from '@/shared/templates/memos/lib/toMemoItems'
import type { MemoSource, MemoSourceType } from '@/shared/templates/memos'

/**
 * What a print article query asks the index for. There is no shared schema for
 * the type - the print article list has its own, carrying columns a memo has no
 * use for - so the memo names the handful of fields it reads: the content the
 * headline is picked out of, and the flow it is filtered by.
 */
const printArticleFields = [
  'document.title',
  'document.content.core_text.data.text',
  'document.rel.flow.uuid',
  'document.meta.tt_print_article.data.date'
] as const

/**
 * The field a print-article source's flow is matched against.
 *
 * The flow is named by title in the template but matched by uuid, which is what
 * the print article list filters on as well: the title the print article
 * carries is full text indexed, so a terms condition on it matches nothing.
 * Resolving the title to a uuid is `useMemoCandidates`' job.
 */
const PRINT_FLOW_UUID_FIELD = 'document.rel.flow.uuid'

/** The usable version a document is set to when it is unpublished. */
const USABLE_VERSION_FIELD = 'heads.usable.version'
const UNPUBLISHED_VERSION = '-1'

/**
 * The field a story filter is matched against, which is not the same one for
 * every source.
 *
 * A planning and an event carry the story with the rel `story`, so it is
 * indexed as its own relation. An article carries the very same link with the
 * rel `subject` instead - see `articleDocumentTemplate`, which rewrites it on
 * the way in - and a terms condition on `document.rel.story.uuid` therefore
 * matches no article at all. The uuid identifies the story either way, so
 * nothing is conditioned on the link's type.
 *
 * A print article carries no story under either rel, which is why the condition
 * sits behind `editorialFilters` with the newsvalue and the section.
 */
const storyUuidField: Record<MemoSourceType, string | undefined> = {
  planning: 'document.rel.story.uuid',
  event: 'document.rel.story.uuid',
  article: 'document.rel.subject.uuid',
  'print-article': undefined
}

/**
 * How a source's date field is filtered:
 *
 * - `instant-range` compares a timestamp field against the whole of the first
 *   day through the whole of the last, as instants.
 * - `date-range` compares a bare date field against the dialog's `YYYY-MM-DD`
 *   values as they are.
 * - `each-date` lists every day in the range, matching the date exactly the way
 *   the print article list does rather than trusting the field to compare.
 */
type MemoDateFilter = 'instant-range' | 'date-range' | 'each-date'

/**
 * The document type and the date field each memo source is filtered on.
 * Plannings and events are planned on a date, articles are picked by
 * publication time, and print articles are made for a print date.
 *
 * `editorialFilters` says the source's documents carry the editorial fields a
 * column is narrowed by - newsvalue, section and story - and can therefore be
 * filtered on them. A print article carries none of them, so conditioning on
 * them would return nothing at all rather than narrow anything.
 *
 * `sortFields` defaults to the date field. Print articles are sorted the way
 * the print article list sorts them, by flow and then by the slot the article
 * fills in it, which is the order they run in the paper.
 *
 * `publishedOnly` limits the source to published documents - usable, at a
 * positive version. A source without it only leaves out the unpublished.
 */
const sourceConfig: Record<MemoSourceType, {
  documentType: string
  dateField: string
  dateFilter: MemoDateFilter
  editorialFilters: boolean
  fields: readonly string[]
  sortFields?: readonly string[]
  publishedOnly?: boolean
}> = {
  planning: {
    documentType: 'core/planning-item',
    dateField: 'document.meta.core_planning_item.data.start_date',
    dateFilter: 'date-range',
    editorialFilters: true,
    fields: planningFields,
    publishedOnly: true
  },
  article: {
    documentType: 'core/article',
    dateField: 'heads.usable.created',
    dateFilter: 'instant-range',
    editorialFilters: true,
    fields: articleFields,
    publishedOnly: true
  },
  'print-article': {
    documentType: 'tt/print-article',
    dateField: 'document.meta.tt_print_article.data.date',
    dateFilter: 'each-date',
    editorialFilters: false,
    fields: printArticleFields,
    sortFields: ['document.rel.flow.title.sort', 'document.title.sort']
  },
  event: {
    documentType: 'core/event',
    dateField: 'document.meta.core_event.data.start',
    dateFilter: 'instant-range',
    editorialFilters: true,
    fields: eventFields,
    publishedOnly: true
  }
}

/**
 * A memo covering a month of events runs past the default page size, and the
 * hits beyond it would simply be missing from the candidate list. This is the
 * largest page `pagination()` accepts; a bigger one silently falls back to 100.
 */
const MEMO_RESULT_SIZE = 1000

/**
 * The largest page the index loads whole documents for. Asking for more is
 * refused outright - "document loading is not allowed for result sets over 200
 * items" - rather than served without the documents, so a query that loads them
 * asks for this much at a time and pages through the rest.
 */
const MEMO_DOCUMENT_RESULT_SIZE = 200

/**
 * What the creation dialog narrows the candidate list by. `newsvalues`,
 * `sections` and `story` are the dialog's own filters; leaving any of them
 * empty means the corresponding condition is left out, not that nothing
 * matches. `story` is a single uuid rather than a list, because one story is
 * what the dialog offers.
 *
 * `from` and `to` are `YYYY-MM-DD` and inclusive, and `timeZone` is what they
 * are days in - the registry timezone, not the browser's. A pair that is not
 * two days is refused rather than asked about - see `validMemoDays`.
 */
export interface MemoQueryFilter {
  from: string
  to: string
  timeZone: string
  newsvalues?: string[]
  sections?: string[]
  story?: string
}

function termsCondition(field: string, values: string[]) {
  return {
    conditions: {
      oneofKind: 'terms' as const,
      terms: TermsQueryV1.create({ field, values })
    }
  }
}

/**
 * Only what is out: usable by checkpoint or state, and at a positive version,
 * which an unpublished document - usable at -1 - is not. The checkpoint is what
 * counts: editing a published article puts its state back to draft, but it is
 * still out.
 */
function publishedConditions() {
  const version = {
    conditions: {
      oneofKind: 'range' as const,
      range: RangeQueryV1.create({ field: USABLE_VERSION_FIELD, gte: '1' })
    }
  }

  const status = {
    conditions: {
      oneofKind: 'bool' as const,
      bool: BoolQueryV1.create({
        should: [
          termsCondition('workflow_checkpoint', ['usable']),
          termsCondition('workflow_state', ['usable'])
        ],
        minimumShouldMatch: 1n
      })
    }
  }

  return [status, version]
}

/** The two days a query covers, once they are known to be days. */
interface MemoDays {
  first: Date
  last: Date
}

/**
 * The day a `YYYY-MM-DD` string names, at midnight local time the way every
 * other date in the dialog is parsed - and nothing at all for a string that
 * names no day.
 *
 * The shape is checked as well as the parse: the parse alone reads `2026-08` as
 * the first of that month, and a day nobody picked is no better than a day that
 * does not parse.
 */
function parseDay(day: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    return undefined
  }

  const parsed = new Date(`${day}T00:00:00`)

  return isNaN(parsed.getTime()) ? undefined : parsed
}

function memoDays(from: string, to: string): MemoDays | undefined {
  const first = parseDay(from)
  const last = parseDay(to)

  if (!first || !last) {
    return undefined
  }

  return { first, last }
}

/**
 * Whether these two days can be asked about at all, which is what a caller
 * checks before asking - `memoQueryParams` builds a query out of nothing else.
 *
 * Says nothing about which way round they run: a backwards range is one the
 * index can answer, with nothing, rather than a pair that names no days.
 */
export function validMemoDays(from: string, to: string): boolean {
  return !!memoDays(from, to)
}

/**
 * The bounds to compare a timestamp date field against: the whole of the first
 * day through the whole of the last, as instants.
 *
 * Comparing a timestamp against a bare `YYYY-MM-DD` means midnight, which would
 * both exclude everything after midnight on the last day and turn a single day
 * memo into an empty interval.
 */
function instantBounds({ first, last }: MemoDays, timeZone: string): { gte: string, lte: string } {
  return {
    gte: fromZonedTime(startOfDay(first), timeZone).toISOString(),
    lte: fromZonedTime(endOfDay(last), timeZone).toISOString()
  }
}

/** Every day of the range, as `YYYY-MM-DD`. */
function datesInRange({ first, last }: MemoDays): string[] {
  // A backwards range covers no days, and is one the dialog holds back rather
  // than one to invent days for - so it asks about the two it names and nothing
  // in between, `eachDayOfInterval` refusing such an interval outright.
  if (last < first) {
    return [...new Set([asDay(first), asDay(last)])]
  }

  return eachDayOfInterval({ start: first, end: last }).map(asDay)
}

function asDay(day: Date): string {
  return format(day, 'yyyy-MM-dd')
}

function dateCondition(
  dateField: string, dateFilter: MemoDateFilter, days: MemoDays, timeZone: string
) {
  if (dateFilter === 'each-date') {
    return termsCondition(dateField, datesInRange(days))
  }

  // The parsed days rather than the strings they were given as, so that what a
  // bare date field is compared against is a day and not whatever arrived.
  const { gte, lte } = dateFilter === 'date-range'
    ? { gte: asDay(days.first), lte: asDay(days.last) }
    : instantBounds(days, timeZone)

  return {
    conditions: {
      oneofKind: 'range' as const,
      range: RangeQueryV1.create({ field: dateField, gte, lte })
    }
  }
}

function constructQuery(
  source: MemoSource, filter: MemoQueryFilter, days: MemoDays, flowUuid?: string
): QueryV1 {
  const { dateField, dateFilter, editorialFilters, publishedOnly } = sourceConfig[source.type]

  const query = QueryV1.create({
    conditions: {
      oneofKind: 'bool',
      bool: BoolQueryV1.create({ must: [] })
    }
  })

  if (query.conditions.oneofKind !== 'bool') {
    return query
  }

  const { must, mustNot } = query.conditions.bool

  must.push(dateCondition(dateField, dateFilter, days, filter.timeZone))

  if (publishedOnly) {
    query.conditions.bool.filter.push(...publishedConditions())
  } else {
    // Unpublishing leaves the usable head in place at version -1, so a withdrawn
    // document is left out explicitly.
    mustNot.push(termsCondition(USABLE_VERSION_FIELD, [UNPUBLISHED_VERSION]))
  }

  if (flowUuid) {
    must.push(termsCondition(PRINT_FLOW_UUID_FIELD, [flowUuid]))
  }

  if (editorialFilters && filter.newsvalues?.length) {
    must.push(termsCondition('document.meta.core_newsvalue.value', filter.newsvalues))
  }

  if (editorialFilters && filter.sections?.length) {
    must.push(termsCondition('document.rel.section.uuid', filter.sections))
  }

  const storyField = storyUuidField[source.type]

  if (editorialFilters && filter.story && storyField) {
    must.push(termsCondition(storyField, [filter.story]))
  }

  return query
}

/**
 * Build the index query parameters for one memo source, spread into the `fetch`
 * the creation dialog asks the index with - `useDocuments` is not what reads
 * them, see `useMemoCandidates`. The extra memo fields are appended so the
 * assignment title and description are available when they exist in the index,
 * along with whatever else this particular source contributes to a memo -
 * except for a source with no editorial fields to speak of, where the
 * assignment extras would only be asking for what cannot be there.
 *
 * The whole source is taken rather than its type alone because a source can
 * restrict the query itself, the way a print-article source names the print
 * flow it is written about. `flowUuid` is that flow, already resolved from the
 * title the source names - a source naming a flow with no uuid to go with it
 * would otherwise be queried as if it named no flow at all.
 *
 * Throws unless both ends of the filter's range are days. The date condition is
 * the one condition every source carries, so a range that is not one leaves
 * nothing to narrow the query by the days at all - and a query for a document
 * type and no days is the whole index rather than a candidate list. Which days
 * to ask about is the caller's to settle before asking, `validMemoDays` being
 * how it tells.
 */
export function memoQueryParams(source: MemoSource, filter: MemoQueryFilter, flowUuid?: string) {
  const days = memoDays(filter.from, filter.to)

  if (!days) {
    throw new Error(
      `Cannot query memo candidates for '${filter.from}'..'${filter.to}': not a YYYY-MM-DD range`
    )
  }

  const {
    documentType, dateField, editorialFilters, fields, sortFields
  } = sourceConfig[source.type]
  const extras = editorialFilters ? memoExtraFields : []

  // Only a planning carries assignments, and the assignment lines of a memo
  // need to know which title belongs to which type - structure the flat fields
  // cannot express, see `assignmentsOf`. Every other source would pay a whole
  // document per hit for something it does not have.
  const loadDocument = source.type === 'planning'

  return {
    documentType,
    // Some of the extras are already part of a source's own schema, and asking
    // the index for the same field twice is only noise.
    fields: [...new Set([...fields, ...extras, ...memoSourceExtraFields[source.type]])],
    query: constructQuery(source, filter, days, flowUuid),
    sort: (sortFields ?? [dateField]).map((field) => SortingV1.create({ field, desc: false })),
    size: loadDocument ? MEMO_DOCUMENT_RESULT_SIZE : MEMO_RESULT_SIZE,
    loadDocument,
    // A page small enough to load documents is not big enough to hold a memo's
    // worth of plannings, so the pages are walked and concatenated. Nothing
    // else needs it: their one page already covers the whole result.
    options: { aggregatePages: loadDocument }
  }
}
